import React from "react";

// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Layouts
import AuthLayout from "./layouts/Auth";
import DashboardLayout from "./layouts/Dashboard";
import SettingsLayout from "./layouts/ProfileSettings";
import ApplicationsLayout from "./layouts/Applications";
import PresentationLayout from "./layouts/Presentation";
import CalendarBookLayout from "./layouts/CalendarBook";

import CalendarDashboard from "./pages/calendar/Dashboard/Dashboard.js";

// Sidebar items

// Guards
import AuthGuard from "./components/guards/AuthGuard";
import SpaceGuard from "./components/guards/SpaceGuard"; // New guard for space-aware routes
import PermissionGuard from "./components/guards/PermissionGuard"; // New guard for permission-based access

// Auth components
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import ResetPassword from "./pages/auth/ResetPassword";
import Page404 from "./pages/auth/Page404";
import Page500 from "./pages/auth/Page500";
import UpdatePassword from "./pages/auth/UpdatePassword";

// Page components
import Blank from "./pages/pages/Blank";
import Pricing from "./pages/pages/Pricing";
import Chat from "./pages/pages/Chat";

// Settings
// Pages Roles
import Roles from "./pages/pages/Roles";
import RolesAdd from "./pages/pages/RolesAdd";
import Users from "./pages/pages/Users";
import Groups from "./pages/pages/Groups";
import Integrations from "./pages/settings/Integrations";
import GoogleAuthCallback from "./pages/calendar/auth/GoogleAuthCallback";
import MicrosoftAuthCallback from "./pages/calendar/auth/MicrosoftAuthCallback";
import BusinessSettings from "./pages/pages/BusinessSettings";
import Profile from "./pages/pages/Profile";
import ChangePassword from "./pages/pages/ChangePassword";
import ForceResetPassword from "./pages/auth/ChangePassword";
import Spaces from "./pages/pages/Spaces";
// Teams
import Teams from "./pages/calendar/Teams/Teams";
import TeamEdit from "./pages/calendar/Teams/TeamEdit";
import TeamInvite from "./pages/calendar/Teams/TeamInvite";
import TeamPreview from "./pages/calendar/Teams/TeamPreview"; // Import the new TeamPreview component
import TeamPublicPage from "./pages/calendar/Teams/TeamPublicPage"; // Import the new TeamPublicPage component

// Event Types
import EventTypes from "./pages/calendar/EventTypes/EventTypes";
import EventTypeAdd from "./pages/calendar/EventTypes/EventTypeAdd";
import EventTypeEdit from "./pages/calendar/EventTypes/EventTypeEdit";
import EventTypePreview from "./pages/calendar/Public/Preview";
import PublicEventsPreview from "./pages/calendar/EventTypes/PublicEventsPreview.js";

// Import components for Routing Forms
import RoutingForms from "./pages/calendar/RoutingForms/index";
import EditRoutingForm from "./pages/calendar/RoutingForms/EditRoutingForm";
import PublicRoutingForm from "./pages/calendar/RoutingForms/PublicRoutingForm";
import RoutingFormSubmit from "./pages/calendar/RoutingForms/RoutingFormSubmit";

// Events
import EventsDashboard from "./pages/Events/Dashboard";
import Events from "./pages/Events/Events";
import EventAdd from "./pages/Events/EventAdd";
import EventView from "./pages/Events/EventView";
import EditorPage from "./pages/Events/EditorPage";

// Landing Page Builder and Preview
import LandingPageBuilder from "./components/Events/Events/Branding/LandingPageBuilder";
import StandaloneLandingPagePreview from "./pages/Events/StandaloneLandingPagePreview";

// Bookings
import Bookings from "./pages/calendar/bookings/Bookings";
import MeetingDetailsPage from "./pages/calendar/bookings/MeetingDetailsPage";

// Meetings
import Meetings from "./pages/calendar/Meetings/Meetings";

// Public Calendar
import Preview from "./pages/calendar/Public/Preview";
import ManageBooking from "./pages/calendar/Public/ManageBooking";

// Availability
import MyAvailabilityAdd from "./pages/calendar/Availibility/index";

// Landing
import Landing from "./pages/presentation/Landing";

// Protected routes
import ProtectedPage from "./pages/protected/ProtectedPage";

// Widgets
import WidgetDashboard from "./pages/Widget/Dashboard/Dashboard.js";
import ChannelConnect from "./pages/Widget/ChannelConnect/ChannelConnect.js";
import ChannelConnectAdd from "./pages/Widget/ChannelConnect/ChannelConnectAdd.js";
import QRConnect from "./pages/Widget/QRConnect/QRConnect.js";
import QRConnectAdd from "./pages/Widget/QRConnect/QRConnectAdd.js";
import Visitors from "./pages/Widget/Visitors/Visitors.js";
import LeadOverview from "./pages/Widget/LeadOverview/LeadOverview.js";
import WidgetSettings from "./pages/Widget/Settings/Settings.js";
import WidgetSupportTickets from "./pages/Widget/SupportTickets/SupportTickets.js";
import WidgetSupportTicketAdd from "./pages/Widget/SupportTickets/SupportTicketAdd.js";
import PublicSupportTicket from "./pages/Widget/SupportTickets/PublicSupportTicket.js";
import IncomingRequests from "./pages/Widget/IncomingRequests/IncomingRequests";
import BotConfigurationPage from "./pages/Widget/ChannelConnect/BotConfiguration/BotConfigurationPage";

// Create a SpaceGuard component to wrap space-aware routes
// This component wraps calendar routes to ensure they have a spaceId
const SpaceAwareRoute = ({ element }) => {
  return <SpaceGuard>{element}</SpaceGuard>;
};

// Create a combined guard for space-aware routes with permissions
const PermissionSpaceRoute = ({ element, requiredPermission }) => {
  return (
    <PermissionGuard requiredPermission={requiredPermission}>
      <SpaceGuard>{element}</SpaceGuard>
    </PermissionGuard>
  );
};

const routes = [
  {
    path: "/",
    element: <PresentationLayout />,
    children: [
      {
        path: "",
        element: <Landing />,
      },
    ],
  },
  {
    path: "dashboard",
    element: <DashboardLayout />,
  },
  {
    path: "settings",
    element: (
      <AuthGuard>
        <SettingsLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "business-settings",
        element: <BusinessSettings />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "change-password",
        element: <ChangePassword />,
      },
      {
        path: "users",
        element: (
          <PermissionGuard requiredPermission="VIEW_USERS">
            <Users />
          </PermissionGuard>
        ),
      },
      {
        path: "roles-permissions",
        element: (
          <PermissionGuard requiredPermission="VIEW_ROLES_PERMISSIONS">
            <Roles />
          </PermissionGuard>
        ),
      },
      {
        path: "roles-permissions-add",
        element: (
          <PermissionGuard requiredPermission="ADD_ROLES_PERMISSIONS">
            <RolesAdd />
          </PermissionGuard>
        ),
      },
      {
        path: "groups",
        element: (
          <PermissionGuard requiredPermission="VIEW_GROUPS">
            <Groups />
          </PermissionGuard>
        ),
      },
      {
        path: "integrations",
        element: <Integrations />,
      },
      {
        path: "space-management",
        element: (
          <PermissionGuard requiredPermission="VIEW_SPACE_MANAGEMENT">
            <Spaces />
          </PermissionGuard>
        ),
      },
    ],
  },
  {
    path: "spaces",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: ":id",
        element: <ProtectedPage />,
      },
    ],
  },
  {
    path: "widget",
    element: (
      <AuthGuard>
        <ApplicationsLayout sidebarItems="widget" />
      </AuthGuard>
    ),
    children: [
      {
        path: "dashboard",
        element: (
          <PermissionSpaceRoute
            element={<WidgetDashboard />}
            requiredPermission="VIEW_WIDGET_DASHBOARD"
          />
        ),
      },
      {
        path: "channel-connect",
        element: (
          <PermissionSpaceRoute
            element={<ChannelConnect />}
            requiredPermission="VIEW_CHANNEL_CONNECT"
          />
        ),
      },
      {
        path: "add-channel-connect",
        element: (
          <PermissionSpaceRoute
            element={<ChannelConnectAdd />}
            requiredPermission="UPDATE_WIDGET_SETTINGS"
          />
        ),
      },
      {
        path: "qr-connect",
        element: (
          <PermissionSpaceRoute
            element={<QRConnect />}
            requiredPermission="VIEW_QR_CONNECT"
          />
        ),
      },
      {
        path: "add-qr-connect",
        element: (
          <PermissionSpaceRoute
            element={<QRConnectAdd />}
            requiredPermission="GENERATE_QR_CODES"
          />
        ),
      },
      {
        path: "visitors",
        element: (
          <PermissionSpaceRoute
            element={<Visitors />}
            requiredPermission="VIEW_VISITOR_ANALYTICS"
          />
        ),
      },
      {
        path: "incoming-requests",
        element: (
          <PermissionSpaceRoute
            element={<IncomingRequests />}
            requiredPermission="VIEW_INCOMING_REQUESTS"
          />
        ),
      },
      {
        path: "lead-overview",
        element: (
          <PermissionSpaceRoute
            element={<LeadOverview />}
            requiredPermission="VIEW_VISITOR_ANALYTICS"
          />
        ),
      },
      {
        path: "settings",
        element: (
          <PermissionSpaceRoute
            element={<WidgetSettings />}
            requiredPermission="UPDATE_WIDGET_SETTINGS"
          />
        ),
      },
      {
        path: "support-tickets",
        element: (
          <PermissionSpaceRoute
            element={<WidgetSupportTickets />}
            requiredPermission="VIEW_SUPPORT_TICKETS"
          />
        ),
      },
      {
        path: "support-ticket/:id",
        element: (
          <PermissionSpaceRoute
            element={<WidgetSupportTicketAdd />}
            requiredPermission="VIEW_SUPPORT_TICKETS"
          />
        ),
      },
      {
        path: "bot-configuration",
        element: (
          <PermissionSpaceRoute
            element={<BotConfigurationPage />}
            requiredPermission="UPDATE_WIDGET_SETTINGS"
          />
        ),
      },
    ],
  },
  {
    path: "calendar",
    element: (
      <AuthGuard>
        <ApplicationsLayout sidebarItems="calendar" />
      </AuthGuard>
    ),
    children: [
      {
        path: "dashboard",
        element: (
          <PermissionSpaceRoute
            element={<CalendarDashboard />}
            requiredPermission="VIEW_CALENDAR"
          />
        ),
      },
      {
        path: "teams",
        element: (
          <PermissionSpaceRoute
            element={<Teams />}
            requiredPermission="MANAGE_TEAM_AVAILABILITY"
          />
        ),
      },
      {
        path: "edit-team/:id",
        element: (
          <PermissionSpaceRoute
            element={<TeamEdit />}
            requiredPermission="MANAGE_TEAM_AVAILABILITY"
          />
        ),
      },
      {
        path: "event-types",
        element: (
          <PermissionSpaceRoute
            element={<EventTypes />}
            requiredPermission="VIEW_EVENT_TYPES"
          />
        ),
      },
      {
        path: "add-event-types",
        element: (
          <PermissionSpaceRoute
            element={<EventTypeAdd />}
            requiredPermission="ADD_EVENT_TYPES"
          />
        ),
      },
      {
        path: "edit-event-type/:id",
        element: (
          <PermissionSpaceRoute
            element={<EventTypeEdit />}
            requiredPermission="UPDATE_EVENT_TYPES"
          />
        ),
      },
      {
        path: "availability",
        element: (
          <PermissionSpaceRoute
            element={<MyAvailabilityAdd />}
            requiredPermission="VIEW_AVAILABILITY"
          />
        ),
      },
      {
        path: "bookings",
        element: (
          <PermissionSpaceRoute
            element={<Bookings />}
            requiredPermission="VIEW_BOOKINGS"
          />
        ),
      },
      {
        path: "bookings/:bookingId",
        element: (
          <PermissionSpaceRoute
            element={<MeetingDetailsPage />}
            requiredPermission="VIEW_CALENDAR"
          />
        ),
      },
      {
        path: "meetings",
        element: (
          <PermissionSpaceRoute
            element={<Meetings />}
            requiredPermission="VIEW_CALENDAR"
          />
        ),
      },
      {
        path: "auth/google/callback",
        element: <GoogleAuthCallback />,
      },
      {
        path: "auth/microsoft/callback",
        element: <MicrosoftAuthCallback />,
      },
      {
        path: "routing-forms",
        element: (
          <PermissionSpaceRoute
            element={<RoutingForms />}
            requiredPermission="VIEW_ROUTING_FORMS"
          />
        ),
      },
      {
        path: "edit-routing-form/:id",
        element: (
          <PermissionSpaceRoute
            element={<EditRoutingForm />}
            requiredPermission="EDIT_ROUTING_FORMS"
          />
        ),
      },
    ],
  },
  {
    path: "events",
    element: (
      <AuthGuard>
        <ApplicationsLayout sidebarItems="events" />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: (
          <PermissionSpaceRoute
            element={<EventsDashboard />}
            requiredPermission="VIEW_EVENTS"
          />
        ),
      },
      {
        path: "dashboard",
        element: (
          <PermissionSpaceRoute
            element={<EventsDashboard />}
            requiredPermission="VIEW_EVENTS"
          />
        ),
      },
      {
        path: "events",
        element: (
          <PermissionSpaceRoute
            element={<Events />}
            requiredPermission="VIEW_EVENTS"
          />
        ),
      },
      {
        path: "add-event",
        element: (
          <PermissionSpaceRoute
            element={<EventAdd />}
            requiredPermission="CREATE_EVENTS"
          />
        ),
      },
      {
        path: "edit-event/:id",
        element: (
          <PermissionSpaceRoute
            element={<EventAdd isEdit={true} />}
            requiredPermission="UPDATE_EVENTS"
          />
        ),
      },
      {
        path: "view-event",
        element: (
          <PermissionSpaceRoute
            element={<EventView />}
            requiredPermission="VIEW_EVENTS"
          />
        ),
      },
      {
        path: "view-event/:id",
        element: (
          <PermissionSpaceRoute
            element={<EventView />}
            requiredPermission="VIEW_EVENTS"
          />
        ),
      },
      {
        path: "builder",
        element: (
          <PermissionSpaceRoute
            element={<EditorPage />}
            requiredPermission="CREATE_EVENTS"
          />
        ),
      },
      {
        path: "landing-page-builder/:id",
        element: <LandingPageBuilder />,
      },
    ],
  },
  // Standalone Landing Page Builder
  {
    path: "landing-page-builder/:id",
    element: (
      <AuthGuard>
        <LandingPageBuilder />
      </AuthGuard>
    ),
  },
  // Standalone Landing Page Preview
  {
    path: "events/preview/:publicUrl",
    element: <StandaloneLandingPagePreview />,
  },
  // Public Support Ticket Creation
  {
    path: "support-ticket/:spaceId",
    element: <PublicSupportTicket />,
  },
  // Public Booking Management (cancel/reschedule)
  {
    path: "public/manage-booking/:token",
    element: <ManageBooking />,
  },
  // Standalone Builder
  {
    path: "builder",
    element: (
      <AuthGuard>
        <EditorPage />
      </AuthGuard>
    ),
  },
  {
    path: "/preview",
    element: <CalendarBookLayout />,
    children: [
      {
        path: "",
        element: <Preview />,
      },
      {
        path: "preview-event-type",
        element: <EventTypePreview />,
      },
      {
        path: "events/preview/:username/:slug",
        element: <EventTypePreview />,
      },
      {
        path: "events/:id", // Route for public events preview by ID (backwards compatibility)
        element: <PublicEventsPreview />,
      },
      {
        path: "events/u/:username", // Route for public events preview by username
        element: <PublicEventsPreview />,
      },
      {
        path: "team/:id", // New route for team preview
        element: <TeamPreview />,
      },
    ],
  },
  {
    path: "/team/:url", // Public-facing team URL (legacy)
    element: <TeamPreview />,
  },
  {
    path: "/teams/:spaceId/:teamUrl", // New space-aware team URL
    element: <TeamPublicPage />,
  },
  {
    path: "/team-invite/:token",
    element: <TeamInvite />,
  },
  {
    path: "/forms/:formId",
    element: <PublicRoutingForm />,
  },
  {
    path: "pages",
    element: <DashboardLayout />,
    children: [
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
      },
      {
        path: "pricing",
        element: <Pricing />,
      },
      {
        path: "chat",
        element: <Chat />,
      },
      {
        path: "blank",
        element: <Blank />,
      },
    ],
  },
  {
    path: "auth",
    element: <AuthLayout />,
    children: [
      {
        path: "sign-in",
        element: <SignIn />,
      },
      {
        path: "sign-up",
        element: <SignUp />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "update-password/:token",
        element: <UpdatePassword />,
      },
      {
        path: "change-password",
        element: <ForceResetPassword />,
      },
      {
        path: "404",
        element: <Page404 />,
      },
      {
        path: "500",
        element: <Page500 />,
      },
    ],
  },
  {
    path: "private",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProtectedPage />,
      },
    ],
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

export default routes;
